import type { HTMLAttributes, RefObject } from 'react';
import { useEffect, useRef } from 'react';
import { useHoverDirty } from 'react-use';

import { cn } from '~/lib/cn';

import { BACKGROUND_COLORS } from './constants';
import { arraySum } from './utils';

interface HorizontalBarProps extends HTMLAttributes<HTMLDivElement> {
  percentageValues: number[];
  className?: string;
  barActive?: RefObject<HTMLDivElement>;
  setBarActive: (element?: RefObject<HTMLDivElement>) => void;
  axisLabels: number[];
}

export const HorizontalBar = ({
  percentageValues,
  className,
  barActive,
  setBarActive,
  axisLabels,
  ...props
}: HorizontalBarProps) => {
  const divRef = useRef<HTMLDivElement>(null);
  const totalPercent = arraySum(percentageValues);
  const isHovering = useHoverDirty(divRef);
  const isBarActive = barActive && barActive !== divRef;
  const isLabelInside = totalPercent >= axisLabels[axisLabels.length - 1] - 5;

  useEffect(() => {
    if (isHovering) {
      setBarActive(divRef);
    } else if (!isHovering && barActive?.current === divRef.current) {
      setBarActive();
    }
  }, [isHovering, divRef, barActive, setBarActive]);

  return (
    <div {...props} className={cn('bg-level-01', className)} ref={divRef}>
      <div className="delay-300 duration-300 ease-out bg-emphasis-default hover:bg-emphasis-subtle">
        <div
          className={cn('cursor-pointer delay-300 duration-300 ease-out', {
            'opacity-stronger': isBarActive,
          })}
        >
          <div
            className="relative flex h-xl grow justify-items-center gap-px delay-300 duration-300 ease-out"
            style={{
              width: `${(totalPercent / axisLabels[axisLabels.length - 1]) * 100}%`,
            }}
          >
            {percentageValues.map((percent, index) => (
              <div
                key={index}
                className={cn(
                  'mr-px flex items-center justify-end duration-300 ease-out',
                  BACKGROUND_COLORS[index % BACKGROUND_COLORS.length],
                  {
                    'rounded-r-xs': index === percentageValues.length - 1,
                  },
                )}
                style={{
                  width: `${((percent / totalPercent) * 100).toFixed(2)}%`,
                }}
              />
            ))}
            <div
              className={cn(
                'absolute flex h-full flex-col justify-center text-caption-md font-semibold',
                isLabelInside
                  ? 'right-0 pr-sm text-strong-on-light'
                  : 'left-full ml-sm',
              )}
            >{`${Math.round(totalPercent)}%`}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

HorizontalBar.displayName = 'HorizontalBar';
